import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../Input/Input";
import styles from "./user-search.module.scss";

export interface UserSearchResult {
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface UserSearchProps {
  onUserSelect: (user: UserSearchResult) => void;
  placeholder?: string;
  excludeUserIds?: string[];
  onSearch?: (query: string) => Promise<UserSearchResult[]>;
  className?: string;
  /** Optional translation function from react-i18next */
  t?: (key: string) => string;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  onUserSelect,
  placeholder,
  excludeUserIds = [],
  onSearch,
  className,
  t,
}) => {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Default translations
  const getText = (key: string): string => {
    if (t) return t(key);

    const defaultTexts: Record<string, string> = {
      "userSearch.defaultPlaceholder": "Search users by @handle...",
      "userSearch.searching": "Searching...",
      "userSearch.noUsersFound": "No users found",
    };

    return defaultTexts[key] || key;
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => !excludeUserIds.includes(user.user_id));
  }, [users, excludeUserIds]);

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (!onSearch || searchQuery.length < 2) {
        setUsers([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await onSearch(searchQuery);
        setUsers(results);
        setIsOpen(true);
      } catch (error) {
        console.error("Error searching users:", error);
        setUsers([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [onSearch]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
  };

  // Debounced search effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, handleSearch]);

  const handleUserClick = (user: UserSearchResult) => {
    onUserSelect(user);
    setQuery(`@${user.handle}`);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (query.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow click events on results
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className={`${styles.userSearch} ${className || ""}`}>
      <Input
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder || getText("userSearch.defaultPlaceholder")}
        className={styles.input}
      />

      {isOpen && (
        <div className={styles.dropdown}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <span>{getText("userSearch.searching")}</span>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className={styles.userList}>
              {filteredUsers.map((user) => (
                <button
                  key={user.user_id}
                  type="button"
                  className={styles.userItem}
                  onClick={() => handleUserClick(user)}
                >
                  <div className={styles.userInfo}>
                    {user.avatar_url && (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.handle}
                        className={styles.avatar}
                      />
                    )}
                    <div className={styles.userDetails}>
                      <div className={styles.handle}>@{user.handle}</div>
                      {user.display_name && (
                        <div className={styles.displayName}>
                          {user.display_name}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className={styles.emptyState}>
              <span>{getText("userSearch.noUsersFound")}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
